import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    API_URL_CURRENT_FRAGMENT,
    del,
    doesNotHaveExpand,
    get,
    HasPagingParameters,
    patch,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import {
    BillToModel,
    CartCollectionModel,
    CartLineCollectionModel,
    CartLineModel,
    CartModel,
    CreditCardDto,
    PaginationModel,
    ProductSubscriptionDto,
    PromotionCollectionModel,
    PromotionModel,
    RealTimeCartInventoryModel,
    SectionOptionDto,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";

export interface GetCartApiParameter extends ApiParameter {
    cartId: string;
    forceRecalculation?: boolean;
    allowInvalidAddress?: boolean;
    expand?: (
        | "tax"
        | "shipping"
        | "creditCardBillingAddress"
        | "paymentOptions"
        | "carriers"
        | "shipTos"
        | "validation"
        | "cartLines"
        | "alsoPurchased"
        | "restrictions"
        | "hiddenproducts"
        | "costCodes"
    )[];
    additionalExpands?: string[];
    alsoPurchasedMaxResults?: number;
}

export interface GetCartsApiParameter extends ApiParameter, HasPagingParameters {
    shipToId?: string;
    billToId?: string;
    status?: string;
    orderNumber?: string;
    fromDate?: string;
    toDate?: string;
    orderTotalOperator?: string;
    orderTotal?: string;
    orderSubtotalOperator?: string;
    orderSubtotal?: string;
    vmiLocationId?: string;
}

export interface AddCartApiParameter extends ApiParameter {
    shipToId?: string;
    billToId?: string;
    notes?: string;
    vmiLocationId?: string;
}

export interface UpdateCartApiParameter extends ApiParameter {
    cart: Cart;
}

export interface GetCartPromotionsApiParameter extends ApiParameter {
    cartId: string;
}

export interface AddCartLinesApiParameter extends ApiParameter {
    cartId: string;
    cartLineCollection: CartLineCollectionModel;
}

export interface AddWishListToCartApiParameter extends ApiParameter {
    wishListId: string;
    changedSharedListLinesQuantities?: { [key: string]: number };
}

export interface ClearCartApiParameter extends ApiParameter {
    cartId: string;
}

export interface RemoveCartLinesApiParameter extends ApiParameter {
    cartId: string;
}

export interface GetCartLineApiParameter extends ApiParameter {
    cartId: string;
    cartLineId: string;
}

export interface UpdateCartLineApiParameter extends ApiParameter {
    cartId: string;
    cartLine: CartLineModel;
}

export interface RemoveCartApiParameter extends ApiParameter {
    cartId: string;
    isAlternateCart?: boolean;
}

export interface RemoveCartLineApiParameter extends ApiParameter {
    cartId: string;
    cartLineId: string;
}

export interface AddProductApiParameter extends ApiParameter {
    productId: string;
    qtyOrdered: number;
    unitOfMeasure: string;
    notes?: string;
    sectionOptions?: Partial<SectionOptionDto>[];
    subscription?: ProductSubscriptionDto;
    properties: any;
}

export interface AddCartPromotionApiParameter extends ApiParameter {
    cartId: string;
    promotionCode: string;
}

export interface RemoveCartPromotionApiParameter extends ApiParameter {
    cartId: string;
    promotionId: string;
}

export interface RemoveCartPromotionsApiParameter extends ApiParameter {
    cartId: string;
}

export interface GetPaymetricResponsePacketApiParameter extends ApiParameter {
    accessToken: string;
}

export interface PaymetricResponseApiResult {
    success: boolean;
    message: string;
    creditCard: CreditCardDto;
}

export interface GetRealTimeCartInventoryApiParameter extends ApiParameter {
    cartId?: string;
    warehouseIds?: string[];
}

const cartsUrl = "/api/v1/carts";

export const invalidAddressException = "Insite.Core.Exceptions.InvalidAddressException";

export type Cart = Omit<CartModel, "billTo" | "shipTo"> & {
    billToId?: string;
    shipToId?: string;
    collectionShipToLabel?: string;
};

export type CartResult = {
    cart: Cart;
    billTo?: BillToModel;
    shipTo?: ShipToModel;
};

export type CartCollectionResult = {
    carts?: Cart[];
    pagination: PaginationModel | null;
};

export async function addProductWithResultReelId(
    parameter: AddProductApiParameter,
): Promise<ServiceResult<CartCollectionModel>> {
    const cartLine = {
        productId: parameter.productId,
        qtyOrdered: parameter.qtyOrdered,
        unitOfMeasure: parameter.unitOfMeasure,
        notes: parameter.notes,
        sectionOptions: parameter.sectionOptions,
        ...(parameter.properties && {
            properties: {
                ...parameter.properties,
            },
        }),
    } as CartLineModel;

    if (parameter.subscription) {
        cartLine.properties = {
            productSubscription: JSON.stringify(parameter.subscription),
        };
    }

    try {
        const cartLineModel = await post(`${cartsUrl}/${API_URL_CURRENT_FRAGMENT}/cartlines`, cartLine);
        return {
            successful: true,
            result: cartLineModel as unknown as CartCollectionModel,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error.status === 403) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
                statusCode: 403,
            };
        }
        throw error;
    }
}
